import { ONBOARDED, ONBOARDING } from "codes";
import menu from "components/Navigation/menu";
import { VITE_INVOICES_PAGE } from "experimental_features";
import { t } from "i18next";
import { useAppSelector } from "redux/hooks";
import NavGroup from "./NavGroup";
import { Business as BusinessIcon } from '@mui/icons-material';
import { ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

// ==============================|| SIDEBAR MENU LIST ||============================== //

export interface Item {
  title: string;
  icon: any;
  activeColor: string;
  iconActiveColor: string;
  iconInactiveColor: string;
  circularBackground: boolean;
  link: string;
  match?: (x: string) => boolean;
  items?: Item[];
  hidden?: boolean;
  disabled?: boolean;
  onClick?: (x?: any) => any;
}

// Funders Menu Component
const FundersMenu = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isActive = location.pathname === '/funders-selection';
  
  const handleClick = () => {
    navigate('/funders-selection');
  };

  return (
    <ListItemButton
      onClick={handleClick}
      sx={{
        borderRadius: `${theme.shape.borderRadius}px`,
        mb: 0.5,
        alignItems: "center",
        py: 1,
        pl: `20px`,
        "&.Mui-selected": {
          bgcolor: `rgba(${theme.palette.primary.mainChannel} / ${theme.palette.action.selectedOpacity})`,
        },
      }}
      selected={isActive}
    >
      <ListItemIcon
        sx={{
          my: "auto",
          minWidth: 36,
          justifyContent: "center",
          color: isActive ? theme.palette.primary.main : theme.palette.inactive.main,
        }}
      >
        <BusinessIcon />
      </ListItemIcon>
      <ListItemText
        primary="View Funders"
        sx={{
          color: isActive ? 'primary.main' : 'primary.main',
          ".MuiTypography-root": {
            fontWeight: isActive ? "bold" : 400,
          },
        }}
      />
    </ListItemButton>
  );
};

const MenuList = () => {
  // Always call all hooks consistently first - this is crucial for React hooks rules
  const APP_STATE = useAppSelector((state) => state.appState.value);
  const USER_PERMISSIONS = useAppSelector((state) => state.appState.user_permission);
  const APP_PERMISSION = useAppSelector((state) => state.appState.permission);
  const [searchParams] = useSearchParams();
  
  // Always call menu() to ensure all hooks are called consistently
  const POST_SALES = [ONBOARDING, ONBOARDED];
  const allMenuItems = menu();
  const menuItems: Item[] = allMenuItems
    .map((item) => {
      if (item.title === "Users")
        return POST_SALES.includes(APP_STATE) ? item : null;
      if (item.title === t("invoices")) return VITE_INVOICES_PAGE ? item : null;
      return item;
    })
    .filter((x) => x !== null) as Item[];
  
  // Check if user is in Funders context after all hooks have been called
  const isFundersContext = APP_PERMISSION === "Funders";
  
  // Check if user is in Investor context (when accessing funders)
  const isInvestorContext = APP_PERMISSION === "Investor";
  
  // Check if we're on investor dashboard with funder_id in URL
  const funderId = searchParams.get('funder_id');
  const isInvestorDashboardWithFunder = funderId && window.location.pathname.includes('/investor/');
  
  // Render appropriate component based on context
  if (isFundersContext && !isInvestorDashboardWithFunder) {
    return <FundersMenu />;
  }
  
  // If we're in investor context OR we're on any investor route with funder_id, show the regular menu
  if (isInvestorContext || isInvestorDashboardWithFunder) {
    return <NavGroup items={menuItems} />;
  }
  
  return <NavGroup items={menuItems} />;
};

export default MenuList;
